// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    OnInit,
    Optional,
    Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';

import { AuthUser } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';
import { WidgetComponentConfig } from '../../widget/widget.component';
import { CMPS_WIDGET_COMPONENT_CONFIG } from '../../widget/widget.metadata';
import { TranslationKeyMap } from '../coaching-insight.i18n';

import {
    AvailablePeriodType,
    CoachingComponentName,
    CoachingComponentType,
    CoachingDateToday,
    CoachingInsightComponent,
    CoachingInsightConfig,
    ColumnConfigItem,
    PersonOfInterest,
} from './models/coaching.global.model';
import { FLOAPerson } from './models/floa.model';
import { ACTIVE_TAB_VIEW, OaPerson, OaPopulation } from './models/oa.model';
import { CoachingInsightWidgetService } from './services/coaching-insight.widget.service';
import { FrontlineOAService } from './services/frontline-oa.service';
import { PersonOfInterestService } from './services/person-of-interest.service';
import { TeamOAService } from './services/team-oa.service';

@Component({
    selector: 'cmps-widget-oa-sm',
    templateUrl: './coaching-insight.widget.component.html',
})
export class CoachingInsightWidgetComponent implements OnChanges, OnDestroy {
    @Input() selectedFavouriteView: PersonOfInterest;
    @Output() floaPersonChange: EventEmitter<FLOAPerson>;
    @Input() coachingInsightUserId: number;

    /** @private */ _componentFloa: CoachingInsightComponent;
    /** @private */ _componentOa: CoachingInsightComponent;

    /** hack to force oa when user has floa component type */
    @Input() preferCoachingComponentTypeOA: boolean;

    @Input() periodTypeId: number;
    @Input() periodTypeDate: Date;
    public coachingViewShortName: Observable<string | number>;
    public coachingComponents: Observable<CoachingInsightComponent[]>;
    public oatoggle: Observable<boolean>;
    public people: Observable<OaPerson[]>;
    public columns: Observable<ColumnConfigItem[]>;
    public favourites: Observable<PersonOfInterest[]>;
    public config: Observable<CoachingInsightConfig>;
    public floa: Observable<FLOAPerson>;
    public favouriteView: Observable<PersonOfInterest>;
    public forceOpenTeamOA: Observable<boolean>;

    hasPendingRefresh: BehaviorSubject<boolean>;
    refreshInterval: any; // interval object

    initialWidgetLoad: boolean = true;

    hasPendingData: boolean = false;
    hasPendingPeople: boolean = false;
    dataFailed: boolean = false;
    renderedMetrics: boolean = false;

    dateRanges: any[];
    selectedPeriodType: string;

    teamcount: number;
    othercount: number;

    rowExpanded: Observable<boolean>;

    configsub: Subscription;
    configsub2: Subscription;
    favouritesub: Subscription;
    peoplesub: Subscription;
    componentsub: Subscription;
    floasub: Subscription;

    oaPeople: OaPopulation = {
        team: [],
        self: null,
        others: [],
    };

    i18n = TranslationKeyMap;

    CoachingComponentName = CoachingComponentName;
    CoachingComponentType = CoachingComponentType;

    refreshTimer: number = 900000;

    cameFromFloa: boolean = false;

    get user(): AuthUser {
        return this._authService.user;
    }

    filterOpen = false;

    periodTypes: AvailablePeriodType[];

    constructor(
        private readonly _coachInsightService: CoachingInsightWidgetService,
        private readonly _personOfInterestService: PersonOfInterestService,
        private readonly _authService: AuthService,
        private readonly _teamOAService: TeamOAService,
        private readonly _floaService: FrontlineOAService,
        private readonly _el: ElementRef,
        private readonly _translateService: TranslateService,
        @Optional()
        @Inject(CMPS_WIDGET_COMPONENT_CONFIG)
        public compConfig: WidgetComponentConfig,
    ) {
        this.floaPersonChange = new EventEmitter();
    }

    ngOnChanges(args: any): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            // eslint-disable-next-line no-prototype-builtins
            args.hasOwnProperty('selectedFavouriteView') ||
            // eslint-disable-next-line no-prototype-builtins
            args.hasOwnProperty('periodTypeId') ||
            // eslint-disable-next-line no-prototype-builtins
            args.hasOwnProperty('periodTypeDate')
        ) {
            this.annihilateInsight(true);
            this.refresh();
            this.initFavourites();

            if (this.periodTypeId) {
                this._coachInsightService.setPeriodTypeId(
                    this.periodTypeId.toString(),
                );
            }

            if (this._floaService.cachedFloa) {
                this._floaService.resetCachedFLOA();
            }

            this.dataFailed = false;
            this._coachInsightService.retrieveCoachingInsightConfig();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    retrieveTeamOA() {
        const service = this._teamOAService;

        const userId = this.selectedFavouriteView
            ? this.selectedFavouriteView.userId
            : this.user
            ? this.user.id
            : null;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const periodTypeId = this._coachInsightService.getPeriodTypeId()
            ? this._coachInsightService.getPeriodTypeId()
            : '4';
        const currentDate = CoachingDateToday;

        const retrievedTeamOA = service.retrieveRawTeamOA(
            userId,
            this.periodTypeId,
            this.periodTypeDate || currentDate,
        );

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const sub = retrievedTeamOA.subscribe((data: any[]) => {
            try {
                const incomingdata = JSON.stringify(data);
                service.hasPendingRefresh.next(false);

                if (
                    typeof incomingdata === 'string' &&
                    typeof service.currentTeam === 'string'
                ) {
                    if (incomingdata !== service.currentTeam) {
                        console.log('team data changed');
                        service.currentTeam = incomingdata;
                        service.hasPendingRefresh.next(true);
                    } else {
                        console.log('team data is unchanged');
                    }
                }
            } catch (e) {
                console.error(e);
                service.hasPendingRefresh.next(false);
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    subscribeToComponents(
        forceExpand: boolean = null,
        restore: boolean = null,
    ) {
        this.componentsub = this.coachingComponents.subscribe(
            (g: CoachingInsightComponent[]) => {
                if (g) {
                    this.loadComponents(g);
                    if (this.componentsub) {
                        this.componentsub.unsubscribe();
                        this.componentsub = null;
                        this.coachingComponents = null;
                    } else {
                        return;
                    }

                    let listening = null;
                    let listening2 = null;

                    const foundFLOA =
                        g.find(
                            (u) =>
                                u.componentTypeId ===
                                CoachingComponentType.FLOA,
                        ) && !this.preferCoachingComponentTypeOA;

                    this.hasPendingPeople = false;

                    if (
                        g.find(
                            (y) =>
                                y.componentTypeId === CoachingComponentType.OA,
                        )
                    ) {
                        this.hasPendingPeople = true;
                        this._teamOAService.retrieveTeamOA(
                            this.selectedFavouriteView
                                ? this.selectedFavouriteView.userId
                                : this.user.id,
                            this.periodTypeId ||
                                this._coachInsightService.getPeriodTypeId() ||
                                this.selectedPeriodType ||
                                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                                (this.periodTypes && this.periodTypes.length
                                    ? this.periodTypes[0].periodTypeId
                                    : 4),
                            this.periodTypeDate || CoachingDateToday,
                        );

                        this.refreshInterval = interval(
                            this.refreshTimer,
                        ).subscribe((val) => {
                            this.retrieveTeamOA();
                        });

                        listening = this.people.subscribe((a) => {
                            this.hasPendingData = false;
                            if (a && listening) {
                                listening.unsubscribe();

                                // look for specifically MISSED OPPS subcomponent
                                setTimeout(() => {
                                    this.setOaPeople(a);
                                    this.teamcount = this.oaPeople.team.length;
                                    this.othercount =
                                        this.oaPeople.others.length;

                                    if (!foundFLOA && this.favouriteView) {
                                        const view =
                                            this._teamOAService.decideInitialView(
                                                this.oaPeople,
                                            );
                                        this._teamOAService.setView(
                                            view,
                                            this._personOfInterestService.getCurrentViewId(),
                                        );
                                    }
                                    this.hasPendingPeople = false;

                                    g.forEach((c: CoachingInsightComponent) => {
                                        const p =
                                            c.coachingInsightSubComponents.find(
                                                (h) =>
                                                    h.subComponentTypeId === 99,
                                            );
                                        if (p) {
                                            p.active = false;
                                            p.displayValue =
                                                this._teamOAService.totalMissedOppsCount;
                                            if (p.displayValue) {
                                                p.active = true;
                                            }
                                        }
                                    });
                                    this._teamOAService.initializeColumnConfig();
                                    this._teamOAService.updateActiveColumns();

                                    if (!foundFLOA) {
                                        this._teamOAService.setView(
                                            ACTIVE_TAB_VIEW.SELF,
                                            null,
                                        );
                                        this.decideIfAutoExpand(
                                            g,
                                            true,
                                            CoachingComponentType.OA,
                                        );
                                    } else {
                                        this.decideIfAutoExpand(
                                            g,
                                            true,
                                            CoachingComponentType.FLOA,
                                        );
                                    }
                                });
                            }
                        });
                    } else {
                        this.hasPendingData = false;
                    }

                    if (foundFLOA) {
                        this.hasPendingPeople = true;
                        this._floaService.retrieveFLOA(
                            this.selectedFavouriteView
                                ? this.selectedFavouriteView.userId
                                : this.user.id,
                            this.periodTypeId ||
                                this._coachInsightService.getPeriodTypeId(),
                            this.periodTypeDate || CoachingDateToday,
                        );

                        const a = this.subscribeToFLOA();
                        listening2 = a.subscribe((h) => {
                            this.hasPendingData = false;
                            if (h && listening2) {
                                listening2.unsubscribe();
                                if (restore === true) {
                                    this._floaService.restoreViewState();
                                }
                                this.decideIfAutoExpand(
                                    g,
                                    forceExpand,
                                    CoachingComponentType.FLOA,
                                );
                                this.hasPendingPeople = false;
                            }
                        });
                    }
                }
            },
        );
    }

    loadComponents(components: CoachingInsightComponent[]): void {
        const floa = components.filter(
            (component) =>
                component.componentTypeId === CoachingComponentType.FLOA,
        );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._componentFloa = floa && floa.length ? floa[0] : null;

        const oa = components.filter(
            (component) =>
                component.componentTypeId === CoachingComponentType.OA,
        );
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._componentOa = oa && oa.length ? oa[0] : null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    subscribeToFLOA() {
        // this.floasub = this.floa.subscribe(loaded => {
        //     if (loaded) {

        //     }
        // });
        return this.floa;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setOaPeopleUsingService() {
        this.setOaPeople(this._teamOAService.getTeam());
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setOaPeople(people) {
        // this.oaPeople.team = people.filter(d => d.oaPopulation === ACTIVE_TAB_VIEW.TEAM);
        // this.oaPeople.others = people.filter(d => d.oaPopulation === ACTIVE_TAB_VIEW.OTHERS);
        // this.oaPeople.self = people.find(d => d.oaPopulation === ACTIVE_TAB_VIEW.SELF);
        this.oaPeople = this._teamOAService.setOaPeople(people);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleCurrentViewChange(
        e: PersonOfInterest,
        forceExpand: boolean = null,
        component?: CoachingComponentType,
        restore: boolean = null,
    ) {
        if (this.initialWidgetLoad) return;

        this.expandCoachingWidget(null);

        this.selectedFavouriteView = e;

        const currentFloa = this._floaService._floa.getValue(); // this is a cheat
        const oaUser = currentFloa.subOrdinates.find(
            (sub) => sub.userId === e.userId,
        );
        this.floaPersonChange.emit(oaUser);

        this.annihilatePeople();
        this.annihilateComponents();

        if (forceExpand === true && component != null) {
            this.cameFromFloa = true;
        }

        setTimeout(() => {
            this.init(forceExpand, restore);
        }, 100);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setCameFromFLOA(state: boolean) {
        this.cameFromFloa = state;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    goBackToStoredFavourite() {
        const storedFave = this._personOfInterestService.setStoredFavourite();
        this.handleCurrentViewChange(
            storedFave,
            true,
            CoachingComponentType.FLOA,
            true,
        );
        this.setCameFromFLOA(false);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    periodTypeChanged($event: string) {
        this._coachInsightService.setPeriodTypeId($event);

        // on change of period type, destroy all data here except the config as the component is not being destroyed
        this.annihilatePeople();
        this.annihilateComponents();

        if (this._floaService.cachedFloa) {
            this._floaService.resetCachedFLOA();
        }

        // setTimeout(() => {
        this.init();
        // }, 300);
    }

    onMetricsRendered($event): void {
        this.renderedMetrics = $event;
    }

    // onReorderTeam($event): void {
    //     this.setOaPeople(this._teamOAService.getTeam());
    // }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    decideIfAutoExpand(
        components?: CoachingInsightComponent[],
        forceExpand: boolean = null,
        component?: CoachingComponentType,
    ) {
        // check if there's a default opened component
        setTimeout(() => {
            const attrs = this._coachInsightService.getComponentAttributes();

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.oaPeople &&
                this.oaPeople.self &&
                !this.oaPeople.team.length &&
                !this.oaPeople.others.length
            ) {
                if (components.length) {
                    if (component) {
                        this._coachInsightService.toggleComponent(
                            component,
                            true,
                        );
                    } else {
                        this._coachInsightService.toggleComponent(
                            CoachingComponentType.OA,
                            true,
                        );
                    }
                } else {
                    this._teamOAService.setForceOpenTeam(true);
                }
            } else if (
                forceExpand === true ||
                (attrs && attrs.defaultExpandWidget === true)
            ) {
                if (component) {
                    this._coachInsightService.toggleComponent(component, true);
                } else {
                    this._coachInsightService.toggleComponent(
                        CoachingComponentType.OA,
                        true,
                    );
                }
            }
        }, 300);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleComponent(comp: CoachingComponentType) {
        this._coachInsightService.toggleComponent(comp);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    expandCoachingWidget(component: CoachingInsightComponent) {
        if (this._el) {
            if (!this._el.nativeElement) return;

            const parent = $(this._el.nativeElement).closest('cmps-widget');

            if (component && component.iconstate === true) {
                parent.addClass('fullwidth');
                parent.removeClass('smallwidth');
                return;
            }

            setTimeout(() => {
                parent.addClass('smallwidth');
                parent.removeClass('fullwidth');
            }, 300);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this.config = null;

        this.annihilateComponents();
        this.annihilatePeople();
        this.annihilateInsight();
        this.annihilateFavourites();

        if (this.refreshInterval) {
            this.refreshInterval.unsubscribe();
        }

        // true means destroy including the config. no params mean reset only summary and oapopulation
        this._coachInsightService.resetWidget(true);
        this._floaService.resetWidget();

        this.floaPersonChange.complete();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    init(forceExpand: boolean = null, restore: boolean = null) {
        this._coachInsightService.initializeSubjects();
        this._teamOAService.initializeSubjects();

        // config is called only 1x per component render
        if (!this.config) {
            this.config = this._coachInsightService.config;
        }

        this.hasPendingData = true;

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.selectedFavouriteView && this.selectedFavouriteView.userId) {
            this._personOfInterestService.setFavourite(
                this.selectedFavouriteView,
            );
        }

        this.coachingComponents = this._coachInsightService.coachingComponents;
        this.favourites = this._personOfInterestService.favourites;
        this.coachingViewShortName = this._coachInsightService.coachingView;
        this.favouriteView = this._personOfInterestService.favouriteView;
        this.forceOpenTeamOA = this._teamOAService.forceOpenTeamOA;
        this.rowExpanded = this._teamOAService.rowExpanded;
        this.people = this._teamOAService.teamoa;
        this.floa = this._floaService.floa;

        // config must be available to get the components

        this.configsub2 = this.config.subscribe((cfg) => {
            if (cfg) {
                const periodtypes = cfg.availablePeriodTypes;

                this.dateRanges = [];
                periodtypes.forEach((p) => {
                    if (p.isDefault != null) {
                        this.dateRanges.push({
                            value: p.periodTypeId.toString(),
                            label:
                                p.periodTypeId === 2
                                    ? this._translateService.instant(
                                          TranslationKeyMap._7439_MTD,
                                      )
                                    : this._translateService.instant(
                                          TranslationKeyMap._7445__30_DAY_ROLLING,
                                      ),
                            isDefault: p.isDefault,
                        });
                    }
                });

                this._coachInsightService.setConfigAttributes(
                    cfg.componentAttributes,
                );

                this._coachInsightService.retrieveCoachingComponents(
                    this._personOfInterestService.getCurrentViewId(),
                    this.periodTypeId ||
                        this._coachInsightService.getPeriodTypeId(),
                );
                this.subscribeToComponents(forceExpand, restore);

                // eslint-disable-next-line no-return-assign
                setTimeout(
                    () =>
                        (this.selectedPeriodType =
                            this._coachInsightService.getPeriodTypeId()),
                );
            }
        });

        this.hasPendingRefresh = this._teamOAService.hasPendingRefresh;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    refresh() {
        this.annihilatePeople();
        this.annihilateComponents();
        this.annihilateInsight();
        this.init();
        this.hasPendingRefresh = this._teamOAService.hasPendingRefresh;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initFavourites() {
        // only retrieve the logged in user's favourites once per page load
        this._personOfInterestService.retrieveFavourites();

        let initSelectFavouriteView = this.favourites.subscribe((data) => {
            // set default favourite when widget is instatiated to the user logged in
            if (data) {
                if (
                    !this.selectedFavouriteView ||
                    !this.selectedFavouriteView.userId
                ) {
                    this._personOfInterestService.setDefaultFavourite();
                }

                setTimeout(() => {
                    this.initialWidgetLoad = false;
                    if (initSelectFavouriteView) {
                        initSelectFavouriteView.unsubscribe();
                    }
                    initSelectFavouriteView = null;
                    this.selectedFavouriteView =
                        this._personOfInterestService.getFavouriteView();
                });
            }
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    annihilateInsight(includeConfig?: boolean) {
        if (this.configsub) this.configsub.unsubscribe();
        if (this.configsub2) this.configsub2.unsubscribe();

        this._coachInsightService.resetWidget(includeConfig);

        if (includeConfig) {
            this.config = null;
            this.configsub2 = null;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    annihilateComponents() {
        this.coachingComponents = null;
        this._coachInsightService.resetComponents();
        this.coachingViewShortName = null;

        this._coachInsightService.setCoachingViewName(null);

        if (this.componentsub) {
            this.componentsub.unsubscribe();
        }

        if (this.refreshInterval) {
            this.refreshInterval.unsubscribe();
        }
        this._teamOAService.oldTeam = '';
        this._teamOAService.currentTeam = '';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    annihilateFavourites() {
        if (this.favouritesub) this.favouritesub.unsubscribe();
        this._personOfInterestService.resetWidget();
        this.favourites = null;
        this.favouriteView = null;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    annihilatePeople() {
        if (this.configsub2) {
            this.configsub2.unsubscribe();
        }

        this.people = null; // observable
        this.floa = null;
        this.forceOpenTeamOA = null;
        this.rowExpanded = null;

        this.setOaPeople([]); // local people collection
        if (this.peoplesub) {
            this.peoplesub.unsubscribe();
        }

        this._teamOAService.resetWidget();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public updateOA() {
        if (this.hasPendingRefresh.getValue()) {
            this.hasPendingRefresh.next(false);
            const newData = this._teamOAService.currentTeam;
            this._teamOAService.oldTeam = newData;
            this._teamOAService.currentTeam = null;
            this.refresh();
        }
    }
}
